/**
 * Admissão de funcionário como assistente (U3 — Stepper Mantine):
 *   1 Dados pessoais → 2 Contrato → 3 Benefícios → 4 Dependentes → 5 Revisão.
 * Salva via POST /funcionarios/completo (transação na camada de serviço).
 *
 * Dependentes (U5): a lista dinâmica persiste registros detalhados por pessoa
 * (nome/nascimento/IR/SF — tabela `dependentes_pessoas`, migration aditiva) e a
 * quantidade para o IR deriva dos marcados; a projeção prefere o detalhe e usa a
 * quantidade manual como fallback.
 */
import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Group, NumberInput, Select, SimpleGrid, Stepper, Switch, Text, TextInput, Title } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { api } from "../api";
import { fmtMoeda, fmtData, fmtCompetencia } from "../format";
import { CompetenciaField } from "../componentes/entradas-mantine";
import { MoedaField, DataField } from "../componentes/entradas-mantine";
import { ErroApiAlert, AvisosAlert } from "../componentes/notificacoes";
import { notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { navegar } from "../roteador";

interface Jornada {
  id: string;
  descricao: string;
  divisorHorasMes: number;
}

const DEPARTAMENTOS = [
  { value: "1", label: "Sócios" },
  { value: "5", label: "Marketing" },
  { value: "6", label: "Comercial" },
  { value: "7", label: "Desenvolvimento" },
  { value: "8", label: "Técnico" },
  { value: "11", label: "Administrativo/Financeiro" },
];

interface VtLinha {
  tarifa: number | null;
  quantidade: number;
}
interface BeneficiarioLinha {
  descricao: string;
  mensalidade: number | null;
  percentualFuncionario: number;
  percentualEmpresa: number;
}
interface DependenteLinha {
  nome: string;
  nascimento: string | null;
  paraIrrf: boolean;
  paraSalarioFamilia: boolean;
}

/** CPF com máscara 000.000.000-00 (persiste só dígitos). */
function CpfField({ valor, onChange }: { valor: string; onChange: (digitos: string) => void }) {
  const mascarar = (t: string) => {
    const d = t.replace(/\D/g, "").slice(0, 11);
    const p = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter(Boolean);
    let out = p.join(".");
    if (d.length > 9) out += `-${d.slice(9)}`;
    return out;
  };
  return (
    <TextInput
      label="CPF"
      description="Só números — a máscara é aplicada na digitação (dado sensível, fica no banco local)."
      placeholder="000.000.000-00"
      value={mascarar(valor)}
      onChange={(e) => onChange(e.currentTarget.value.replace(/\D/g, "").slice(0, 11))}
      inputMode="numeric"
    />
  );
}

export function AdmissaoWizard() {
  const [passo, setPasso] = useState(0);
  const [jornadas, setJornadas] = useState<Jornada[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);
  // P2 — admissão com vigência cobrindo meses fechados: sucesso com aviso amarelo
  const [avisosAdmissao, setAvisosAdmissao] = useState<{ nome: string; id: string; avisos: string[] } | null>(null);

  // passo 1 — dados pessoais
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [matricula, setMatricula] = useState<number | null>(null);
  const [convenia, setConvenia] = useState<number | null>(null);
  // passo 2 — contrato
  const [salario, setSalario] = useState<number | null>(null);
  const [cargo, setCargo] = useState("");
  const [cbo, setCbo] = useState("");
  const [departamento, setDepartamento] = useState("7");
  const [jornadaId, setJornadaId] = useState("jornada-200");
  const [admissao, setAdmissao] = useState<string | null>(null);
  const [vigencia, setVigencia] = useState<number | null>(null);
  // passo 3 — benefícios
  const [vtAtivo, setVtAtivo] = useState(false);
  const [vtLinhas, setVtLinhas] = useState<VtLinha[]>([{ tarifa: null, quantidade: 2 }]);
  const [vr, setVr] = useState(false);
  const [caju, setCaju] = useState(false);
  const [homeOffice, setHomeOffice] = useState(false);
  const [seguro, setSeguro] = useState(false);
  const [planoAtivo, setPlanoAtivo] = useState(false);
  const [beneficiarios, setBeneficiarios] = useState<BeneficiarioLinha[]>([
    { descricao: "titular", mensalidade: null, percentualFuncionario: 10, percentualEmpresa: 90 },
  ]);
  // passo 4 — dependentes
  const [dependentes, setDependentes] = useState<DependenteLinha[]>([]);

  useEffect(() => {
    api
      .get<{ jornadas: Jornada[] }>("/jornadas")
      .then((r) => setJornadas(r.jornadas))
      .catch(setErro);
  }, []);

  const passo1Ok = nome.trim().length > 0 && (cpf === "" || cpf.length === 11);
  const passo2Ok = salario !== null && salario > 0 && admissao !== null && vigencia !== null && jornadaId !== "";
  const dependentesIrrf = dependentes.filter((d) => d.paraIrrf).length;

  function avancar() {
    if (passo === 0 && !passo1Ok) return;
    if (passo === 1 && !passo2Ok) return;
    setPasso((p) => Math.min(4, p + 1));
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const corpo = {
        nome: nome.trim(),
        cpf: cpf || null,
        matriculaDominio: matricula,
        idConvenia: convenia,
        contrato: {
          vigenciaInicio: vigencia!,
          salarioBaseCentavos: salario!,
          cargo: cargo || undefined,
          cbo: cbo || undefined,
          departamentoDominio: Number(departamento),
          jornadaId,
          admissao: admissao!,
        },
        dependentesIrrf,
        // U5: a lista detalhada também é gravada (registros por pessoa)
        dependentesLista: dependentes
          .filter((d) => d.nome.trim())
          .map((d) => ({ nome: d.nome.trim(), nascimento: d.nascimento, paraIrrf: d.paraIrrf, paraSalarioFamilia: d.paraSalarioFamilia })),
        beneficios: {
          vr,
          cajuMais: caju,
          auxilioHomeOffice: homeOffice,
          seguroVida: seguro,
          vt: vtAtivo ? vtLinhas.filter((l) => l.tarifa !== null).map((l) => ({ tarifaCentavos: l.tarifa!, quantidade: l.quantidade })) : null,
          planoSaude: planoAtivo
            ? beneficiarios
                .filter((b) => b.mensalidade !== null)
                .map((b) => ({
                  descricao: b.descricao || "beneficiário",
                  mensalidadeCentavos: b.mensalidade!,
                  percentualFuncionario: b.percentualFuncionario,
                  percentualEmpresa: b.percentualEmpresa,
                }))
            : null,
        },
      };
      const r = await api.post<{ funcionario: { id: string; nome: string }; avisos?: string[] }>("/funcionarios/completo", corpo);
      // P2 — carga histórica: com avisos (vigência cobrindo meses fechados),
      // mostra o alerta amarelo ANTES de navegar (o dono precisa ver o efeito)
      if (r.avisos && r.avisos.length > 0) {
        setAvisosAdmissao({ nome: r.funcionario.nome, id: r.funcionario.id, avisos: r.avisos });
        setSalvando(false);
        return;
      }
      notificarOk(`${r.funcionario.nome} admitido(a) com contrato e benefícios.`);
      navegar(`/funcionarios/${r.funcionario.id}`);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
      setSalvando(false);
    }
  }

  return (
    <>
      <Title order={2} mb="md">Admissão de funcionário</Title>
      <ErroApiAlert erro={erro} />
      {avisosAdmissao ? (
        <Card withBorder shadow="xs">
          <Title order={4} mb="sm">{avisosAdmissao.nome} foi admitido(a) com sucesso.</Title>
          <AvisosAlert avisos={avisosAdmissao.avisos} />
          <Group justify="flex-end" mt="sm">
            <Button onClick={() => navegar(`/funcionarios/${avisosAdmissao.id}`)}>Ir para o cadastro</Button>
          </Group>
        </Card>
      ) : (
      <>
      <Stepper active={passo} onStepClick={(i) => i < passo && setPasso(i)} size="sm" mb="md">
        <Stepper.Step label="1. Dados pessoais" />
        <Stepper.Step label="2. Contrato" />
        <Stepper.Step label="3. Benefícios" />
        <Stepper.Step label="4. Dependentes" />
        <Stepper.Step label="5. Revisão" />
      </Stepper>

      {passo === 0 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb="sm">1. Dados pessoais</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Nome oficial completo"
              description="Como sai no retorno da contabilidade e no Convenia (maiúsculas, sem abreviar)."
              value={nome}
              onChange={(e) => setNome(e.currentTarget.value.toUpperCase())}
              required
              error={nome.trim() === "" ? "obrigatório" : undefined}
            />
            <CpfField valor={cpf} onChange={setCpf} />
            <NumberInput
              label="Matrícula Domínio"
              description="Código do empregado no sistema da contabilidade — é a chave de matching do retorno mensal. Sem ela a pessoa não entra na folha calculada."
              value={matricula ?? ""}
              onChange={(v) => setMatricula(typeof v === "number" ? v : null)}
              min={1}
              hideControls
            />
            <NumberInput
              label="ID Convenia"
              description="Identificador da pessoa no sistema de RH Convenia (importação de eventos variáveis). Pode ficar para depois."
              value={convenia ?? ""}
              onChange={(v) => setConvenia(typeof v === "number" ? v : null)}
              min={1}
              hideControls
            />
          </SimpleGrid>
        </Card>
      )}

      {passo === 1 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb="sm">2. Contrato</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <MoedaField label="Salário base mensal" description="Valor cheio do mês — pró-rata no mês de admissão é automática." valor={salario} onChange={setSalario} required />
            <TextInput label="Cargo" description="Como no retorno (ex.: PROGRAMADOR JUNIOR - NÍVEL III)." value={cargo} onChange={(e) => setCargo(e.currentTarget.value.toUpperCase())} />
            <TextInput label="CBO" description="Classificação Brasileira de Ocupações (6 dígitos, sem traço)." value={cbo} onChange={(e) => setCbo(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" />
            <Select label="Departamento" data={DEPARTAMENTOS} value={departamento} onChange={(v) => setDepartamento(v ?? "7")} />
            <Select
              label="Jornada"
              description="Divisor do salário-hora (200 = 40h/semana; 220 = 44h)."
              data={jornadas.map((j) => ({ value: j.id, label: `${j.descricao} (divisor ${j.divisorHorasMes})` }))}
              value={jornadaId}
              onChange={(v) => setJornadaId(v ?? "jornada-200")}
            />
            <DataField label="Data de admissão" description="Dia do início do vínculo." valor={admissao} onChange={setAdmissao} required />
            <div>
              <Text size="sm" fw={600} mb={2}>Vale na folha a partir de</Text>
              <CompetenciaField valor={vigencia} onChange={setVigencia} />
              <Text size="xs" c="dimmed" mt={2}>
                Primeira competência em que o contrato vale (pode ser retroativa — fase de carga histórica; os meses já
                fechados não mudam).
              </Text>
            </div>
          </SimpleGrid>
        </Card>
      )}

      {passo === 2 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb="sm">3. Benefícios (defaults desmarcados — marque o que se aplica)</Title>
          <Group gap="xl" align="flex-start" wrap="wrap">
            <div>
              <Switch
                label="Vale-transporte"
                description="Desconto = min(6% do salário, custo das tarifas)."
                checked={vtAtivo}
                onChange={(e) => setVtAtivo(e.currentTarget.checked)}
                mb="sm"
              />
              {vtAtivo &&
                vtLinhas.map((l, i) => (
                  <Group key={i} mb="xs" align="flex-end">
                    <MoedaField
                      label={i === 0 ? "Tarifa (ida+volta)" : ""}
                      valor={l.tarifa}
                      onChange={(v) => setVtLinhas((ls) => ls.map((x, j) => (j === i ? { ...x, tarifa: v } : x)))}
                      w={130}
                    />
                    <NumberInput
                      label={i === 0 ? "Quantidade/dia" : ""}
                      value={l.quantidade}
                      onChange={(v) => setVtLinhas((ls) => ls.map((x, j) => (j === i ? { ...x, quantidade: Number(v) || 1 } : x)))}
                      min={1}
                      max={4}
                      w={90}
                    />
                    {vtLinhas.length > 1 && (
                      <Button variant="subtle" color="red" size="xs" onClick={() => setVtLinhas((ls) => ls.filter((_, j) => j !== i))}>
                        <IconTrash size={14} />
                      </Button>
                    )}
                  </Group>
                ))}
              {vtAtivo && vtLinhas.length < 2 && (
                <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setVtLinhas((ls) => [...ls, { tarifa: null, quantidade: 2 }])}>
                  segunda tarifa
                </Button>
              )}
            </div>
            <div>
              <Switch label="Vale-refeição" description="Crédito do mês seguinte, sem desconto do funcionário." checked={vr} onChange={(e) => setVr(e.currentTarget.checked)} mb="sm" />
              <Switch label="Caju+ (Gympass/Conexa)" description="Custo empresa 25,00 + simbólico 1,00." checked={caju} onChange={(e) => setCaju(e.currentTarget.checked)} mb="sm" />
              <Switch label="Auxílio home office" description="Custo empresa 250,00 + simbólico 1,00." checked={homeOffice} onChange={(e) => setHomeOffice(e.currentTarget.checked)} mb="sm" />
              <Switch label="Seguro de vida" description="Custo empresa 27,78 + simbólico 1,00." checked={seguro} onChange={(e) => setSeguro(e.currentTarget.checked)} mb="sm" />
            </div>
            <div>
              <Switch
                label="Plano de saúde"
                description="Com beneficiários e rateio por pessoa."
                checked={planoAtivo}
                onChange={(e) => setPlanoAtivo(e.currentTarget.checked)}
                mb="sm"
              />
              {planoAtivo &&
                beneficiarios.map((b, i) => (
                  <Group key={i} mb="xs" align="flex-end" wrap="nowrap">
                    <TextInput
                      label={i === 0 ? "Beneficiário" : ""}
                      value={b.descricao}
                      onChange={(e) => {
                        // o valor é lido ANTES do updater — dentro dele o currentTarget já é null
                        const v = e.currentTarget.value;
                        setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, descricao: v } : x)));
                      }}
                      w={150}
                    />
                    <MoedaField
                      label={i === 0 ? "Mensalidade" : ""}
                      valor={b.mensalidade}
                      onChange={(v) => setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, mensalidade: v } : x)))}
                      w={110}
                    />
                    <NumberInput
                      label={i === 0 ? "% func." : ""}
                      value={b.percentualFuncionario}
                      onChange={(v) => setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, percentualFuncionario: Number(v) || 0 } : x)))}
                      min={0}
                      max={100}
                      w={70}
                    />
                    <NumberInput
                      label={i === 0 ? "% empresa" : ""}
                      value={b.percentualEmpresa}
                      onChange={(v) => setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, percentualEmpresa: Number(v) || 0 } : x)))}
                      min={0}
                      max={100}
                      w={80}
                    />
                    {beneficiarios.length > 1 && (
                      <Button variant="subtle" color="red" size="xs" onClick={() => setBeneficiarios((ls) => ls.filter((_, j) => j !== i))}>
                        <IconTrash size={14} />
                      </Button>
                    )}
                  </Group>
                ))}
              {planoAtivo && (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setBeneficiarios((ls) => [...ls, { descricao: "dependente: ", mensalidade: null, percentualFuncionario: 50, percentualEmpresa: 50 }])}
                >
                  beneficiário
                </Button>
              )}
            </div>
          </Group>
        </Card>
      )}

      {passo === 3 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb="sm">4. Dependentes</Title>
          <Text size="sm" c="dimmed" mb="sm">
            Monte a lista: a <strong>quantidade para o IR</strong> deriva dos marcados ({dependentesIrrf} nesta admissão) e o
            detalhe por pessoa (nome/nascimento) fica gravado no cadastro detalhado (U5).
          </Text>
          {dependentes.map((d, i) => (
            <Group key={i} mb="xs" align="flex-end" wrap="nowrap">
              <TextInput
                label={i === 0 ? "Nome" : ""}
                value={d.nome}
                onChange={(e) => {
                  // o valor é lido ANTES do updater — dentro dele o currentTarget já é null
                  const v = e.currentTarget.value;
                  setDependentes((ls) => ls.map((x, j) => (j === i ? { ...x, nome: v } : x)));
                }}
                w={220}
              />
              <DataField label={i === 0 ? "Nascimento" : ""} valor={d.nascimento} onChange={(v) => setDependentes((ls) => ls.map((x, j) => (j === i ? { ...x, nascimento: v } : x)))} w={150} />
              <Checkbox
                label="IR"
                checked={d.paraIrrf}
                onChange={(e) => {
                  const c = e.currentTarget.checked;
                  setDependentes((ls) => ls.map((x, j) => (j === i ? { ...x, paraIrrf: c } : x)));
                }}
              />
              <Checkbox
                label="Salário-família"
                checked={d.paraSalarioFamilia}
                onChange={(e) => {
                  const c = e.currentTarget.checked;
                  setDependentes((ls) => ls.map((x, j) => (j === i ? { ...x, paraSalarioFamilia: c } : x)));
                }}
              />
              <Button variant="subtle" color="red" size="xs" onClick={() => setDependentes((ls) => ls.filter((_, j) => j !== i))}>
                <IconTrash size={14} />
              </Button>
            </Group>
          ))}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setDependentes((ls) => [...ls, { nome: "", nascimento: null, paraIrrf: true, paraSalarioFamilia: false }])}
          >
            adicionar dependente
          </Button>
        </Card>
      )}

      {passo === 4 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb="sm">5. Revisão — confira antes de gravar</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
            <Card withBorder>
              <Text fw={700} mb={4}>Dados pessoais</Text>
              <Text size="sm">{nome || "—"}</Text>
              <Text size="sm" c="dimmed">
                CPF {cpf ? cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"} · Domínio {matricula ?? "—"} · Convenia {convenia ?? "—"}
              </Text>
            </Card>
            <Card withBorder>
              <Text fw={700} mb={4}>Contrato</Text>
              <Text size="sm">
                {salario !== null ? fmtMoeda(salario) : "—"} · {cargo || "sem cargo"} · depto {DEPARTAMENTOS.find((d) => d.value === departamento)?.label}
              </Text>
              <Text size="sm" c="dimmed">
                {jornadas.find((j) => j.id === jornadaId)?.descricao} · admissão {admissao ? fmtData(`${admissao}T00:00:00.000Z`) : "—"} · vale de {vigencia ? fmtCompetencia(vigencia) : "—"}
              </Text>
            </Card>
            <Card withBorder>
              <Text fw={700} mb={4}>Benefícios</Text>
              <Text size="sm">
                {[
                  vtAtivo && `VT (${vtLinhas.filter((l) => l.tarifa !== null).length} tarifa(s))`,
                  vr && "VR",
                  caju && "Caju+",
                  homeOffice && "Home office",
                  seguro && "Seguro de vida",
                  planoAtivo && `Plano (${beneficiarios.filter((b) => b.mensalidade !== null).length} beneficiário(s))`,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Nenhum benefício marcado"}
              </Text>
            </Card>
            <Card withBorder>
              <Text fw={700} mb={4}>Dependentes</Text>
              <Text size="sm">
                {dependentes.length === 0 ? "Nenhum" : `${dependentes.length} na lista · ${dependentesIrrf} para o IR`}
              </Text>
            </Card>
          </SimpleGrid>
          <Button size="md" onClick={() => void salvar()} loading={salvando} disabled={!passo1Ok || !passo2Ok}>
            Admitir {nome.split(" ")[0] || "funcionário"}
          </Button>
        </Card>
      )}

      <Group justify="space-between" mt="md">
        <Button variant="default" disabled={passo === 0} onClick={() => setPasso((p) => Math.max(0, p - 1))}>
          ← Voltar
        </Button>
        {passo < 4 ? (
          <Button onClick={avancar} disabled={(passo === 0 && !passo1Ok) || (passo === 1 && !passo2Ok)}>
            Avançar →
          </Button>
        ) : (
          <span />
        )}
      </Group>
      </>
      )}
    </>
  );
}
